import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { attorneys, stats } from "../mock";
import { Award, ScrollText, Users, Handshake, FileCheck2, Gavel, ArrowRight, Scale } from "lucide-react";

const iconMap = { 1: Users, 2: Handshake, 3: FileCheck2, 4: Gavel };

export default function About() {
  return (
    <>
      <Header />
      <section
        className="h-[42vh] min-h-[280px] w-full bg-cover bg-center relative flex items-end"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,20,40,0.85), rgba(10,20,40,0.85)), url('https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=1920&q=80')",
        }}
      >
        <div className="container-x pb-14 text-white">
          <span className="text-[#c9a961] uppercase tracking-[0.35em] text-xs font-semibold">Our Story</span>
          <h1 className="font-serif-display text-4xl md:text-6xl mt-3">About Us</h1>
          <div className="text-sm text-neutral-300 mt-3">
            <Link to="/" className="hover:text-[#c9a961]">Home</Link> <span className="mx-2">/</span> About
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div className="relative">
            <div
              className="relative rounded-sm overflow-hidden h-[520px] flex flex-col items-center justify-center text-center px-8"
              style={{
                background:
                  "linear-gradient(160deg, #0e1b34 0%, #14264a 55%, #0e1b34 100%)",
              }}
            >
              <div
                className="absolute inset-0 pointer-events-none opacity-40"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 25% 20%, rgba(201,169,97,0.28) 0, transparent 45%), radial-gradient(circle at 80% 85%, rgba(201,169,97,0.18) 0, transparent 45%)",
                }}
              />
              <div className="relative z-10 w-40 h-40 rounded-full border-2 border-[#c9a961] flex items-center justify-center mb-6 bg-[#0a1428]/40">
                <div className="w-32 h-32 rounded-full border border-[#c9a961]/50 flex items-center justify-center">
                  <Scale className="text-[#c9a961]" size={56} />
                </div>
              </div>
              <div className="relative z-10">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#c9a961] font-semibold">
                  Est. 2004
                </div>
                <h3 className="font-serif-display text-3xl md:text-4xl text-white mt-3 leading-tight">
                  Pravin Gupta
                  <br />
                  <span className="text-[#c9a961]">& Associates</span>
                </h3>
                <div className="mt-4 h-[1px] w-16 bg-[#c9a961] mx-auto" />
                <p className="text-neutral-300 text-sm mt-4 italic">
                  Voice of Justice. Advocates & Solicitors.
                </p>
              </div>
            </div>
          </div>
          <div>
            <span className="text-[#b28e42] uppercase tracking-[0.35em] text-xs font-semibold">About the Firm</span>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#0e1b34] mt-3 mb-6 gold-underline">
              Pravin Gupta & Associates.
            </h2>
            <p className="text-neutral-700 leading-relaxed mb-4">
              Founded in 2004, Pravin Gupta & Associates is a full-service law firm based in Mumbai with a
              nationwide practice. We advise individuals, entrepreneurs, small businesses and large corporates
              on the full spectrum of Indian law.
            </p>
            <p className="text-neutral-700 leading-relaxed mb-4">
              The firm is led by Adv. Pravin K. Gupta, Advocate & Solicitor. Under his leadership, the firm
              handles matters across Property, Redevelopment, Corporate, Criminal, Matrimonial, DRT, MACT and
              Income Tax practice areas.
            </p>
            <p className="text-neutral-700 leading-relaxed mb-8">
              We believe that the practice of law is a craft that combines the discipline of scholarship with
              the agility of technology. We invest heavily in both.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="flex items-start gap-3 p-4 border border-neutral-200 rounded-sm">
                <Award className="text-[#c9a961]" />
                <div>
                  <div className="font-semibold text-[#0e1b34]">Award Winning</div>
                  <div className="text-xs text-neutral-500">Featured firm 2022, 2023</div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-neutral-200 rounded-sm">
                <ScrollText className="text-[#c9a961]" />
                <div>
                  <div className="font-semibold text-[#0e1b34]">20+ Years</div>
                  <div className="text-xs text-neutral-500">Serving clients since 2004</div>
                </div>
              </div>
            </div>

            <Link to="/contact" className="btn-gold">
              Get in Touch <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0e1b34]">
        <div className="container-x grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s) => {
            const Icon = iconMap[s.id];
            return (
              <div key={s.id} className="text-center text-white">
                <Icon className="text-[#c9a961] mx-auto mb-3" size={30} />
                <div className="font-serif-display text-4xl font-bold">{s.value.toLocaleString()}</div>
                <div className="text-[#c9a961] text-xs uppercase tracking-[0.25em] mt-1">{s.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-24">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-[#b28e42] uppercase tracking-[0.35em] text-xs font-semibold">Our People</span>
            <h2 className="font-serif-display text-4xl text-[#0e1b34] mt-3 gold-underline center">
              Meet Our Team
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {attorneys.slice(0, 10).map((a) => {
              const initials = a.name
                .split(" ")
                .map((n) => n[0])
                .slice(0, 2)
                .join("")
                .toUpperCase();
              return (
                <div key={a.id} className="attorney-card bg-white rounded-sm overflow-hidden border border-neutral-200">
                  <div
                    className="aspect-[3/4] flex items-center justify-center relative"
                    style={{ background: "linear-gradient(160deg, #0e1b34, #14264a)" }}
                  >
                    <div
                      className="absolute inset-0 opacity-40"
                      style={{
                        backgroundImage:
                          "radial-gradient(circle at 20% 20%, rgba(201,169,97,0.28) 0, transparent 45%), radial-gradient(circle at 80% 85%, rgba(201,169,97,0.18) 0, transparent 45%)",
                      }}
                    />
                    <div className="relative z-10 w-24 h-24 rounded-full border-2 border-[#c9a961] flex items-center justify-center bg-[#0a1428]/40">
                      <span className="font-serif-display text-3xl text-[#c9a961] font-semibold tracking-wider">
                        {initials}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 text-center">
                    <h4 className="font-serif-display text-[#0e1b34] font-semibold">{a.name}</h4>
                    <p className="text-[#b28e42] text-[11px] uppercase tracking-[0.2em] mt-1 font-semibold">{a.role}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
